import { useState } from 'react';
import { useRecoilValue, useResetRecoilState, useSetRecoilState } from 'recoil';
import { useConditionalEffect } from 'react-delta';
import BootstrapButtonGroup from 'react-bootstrap/ButtonGroup';
import SkipPreviousIcon from '@mui/icons-material/SkipPrevious';
import SkipNextIcon from '@mui/icons-material/SkipNext';
import FlashOnIcon from '@mui/icons-material/FlashOn';
import StarIcon from '@mui/icons-material/Star';
import ShareIcon from '@mui/icons-material/Share';
import SettingsIcon from '@mui/icons-material/Settings';
import HelpIcon from '@mui/icons-material/Help';

import * as K from 'common/constants';
import { HELP_ROUTE } from 'components/help/constants';
import ELEMENT from 'common/element';
import { LogEntry, LogEntryEdge, QueryType, SortOrder } from 'common/graphqlTypes';
import { PlaceholderLogEntryEdge, useEventCache } from 'hooks/useLRQ/useEventCache';
import { generateLogEntriesRequest, LRQRequest } from 'hooks/useLRQ/useLRQ';
import { downloadFile } from 'service/browser';
import useCopyToClipboard from 'hooks/useCopyToClipboard';
import { getRequestId } from 'common/util/lrqRequest';
import {
  absoluteResolvedUrlState,
  completeFilterState,
  isCrossTeamSearchState,
  liveTailPageUrlState,
  queryTimeRangeState,
  searchParamState,
  teamEmailsState,
} from 'components/common/Managers/RouteStateManager.state';
import {
  anchorLogRequestsState,
  eventSortOrderState,
  isAtEndTimeState,
  isAtStartTimeState,
  isScrollReadyState,
  paginatedLogRequestsState,
} from 'components/search/SearchLogsPage/SearchStateManager.state';
import { facetInfoState } from 'components/search/facets.state';
import { useShortUrl } from 'hooks/useShortUrl';

import { applySettingsToUrl } from 'common/util/settings';
import { formatSearchResults } from 'common/util/format';
import { getDownloadFilename } from 'common/util/file';

import LRQRequestManager from 'components/common/Managers/LRQRequestManager';
import { timezoneState } from 'components/common/Managers/PreferencesManager.state';
import { settingsState } from 'components/common/Managers/SettingsManager.state';
import Button from 'components/common/Button/Button';
import Toolbar from 'components/common/Toolbar/Toolbar';
import SearchActionsDropdown, { ActionMenuItemConfig, SearchActions } from 'components/search/SearchActionsDropdown';
import LogViewSettingsDialog from 'components/search/SearchLogsPage/LogViewSettingsDialog';
import {
  pluginSearchResultsMenuItemsState,
  viewDisplaySettingsState,
} from 'components/common/Managers/ViewStateManager.state';
import { TriggerType } from 'components/alerts/SaveAlertDialog';
import {
  isLiveTailActiveState,
  logResultsScrollTimeMarker,
  useLogResultsState,
  visibleCenterIndexState,
} from 'components/search/SearchLogsPage/LogResultsContainer.hooks';
import { accountDataState } from 'components/account/account.state';

import LogLinesToolbarButtons from './LogLinesToolbarButtons';

import 'components/common/Button/Button.scss';
import './LogResultsToolbar.scss';

// button icons
const skipPreviousIcon = <SkipPreviousIcon fontSize="small" />;
const skipNextIcon = <SkipNextIcon fontSize="small" />;
const flashOnIcon = <FlashOnIcon fontSize="small" />;
const starIcon = <StarIcon fontSize="small" />;
const shareIcon = <ShareIcon fontSize="small" />;
const settingsIcon = <SettingsIcon fontSize="small" />;
const helpIcon = <HelpIcon fontSize="small" />;

const NUM_DOWNLOAD_ENTRIES = 10000;

/**
 * LogResultsToolbar is a toolbar with buttons that allow the user to interact with search results.
 */
function LogResultsToolbar(): JSX.Element {
  const resetIsScrollReady = useResetRecoilState(isScrollReadyState);
  const resetVisibleCenterIndex = useResetRecoilState(visibleCenterIndexState);
  const setAnchorLogRequests = useSetRecoilState(anchorLogRequestsState);
  const setPaginatedLogRequests = useSetRecoilState(paginatedLogRequestsState);
  const [showSettingsDialog, setShowSettingsDialog] = useState(false);
  const queryTimeRange = useRecoilValue(queryTimeRangeState);
  const completeFilter = useRecoilValue(completeFilterState);
  const { startTime, endTime, startTimeNs, endTimeNs, requestTime } = queryTimeRange;
  const isCrossTeamSearch = useRecoilValue(isCrossTeamSearchState);
  const teamEmails = useRecoilValue(teamEmailsState);
  const [logEntriesRequest, setLogEntriesRequest] = useState<LRQRequest | null>(null);
  const logEntriesDataId = getRequestId(logEntriesRequest, QueryType.LOG);
  const logEntriesQueryResult = useEventCache([logEntriesDataId]);
  const setTimeMarker = useSetRecoilState(logResultsScrollTimeMarker);
  const settings = useRecoilValue(settingsState);
  const timezone = useRecoilValue(timezoneState);
  const absoluteUrl = useRecoilValue(absoluteResolvedUrlState);
  const [downloadFormat, setDownloadFormat] = useState<K.DATA_FORMAT>(K.DATA_FORMAT.TEXT);
  const sortOrder = useRecoilValue(eventSortOrderState);
  const isAtStartTime = useRecoilValue(isAtStartTimeState);
  const isAtEndTime = useRecoilValue(isAtEndTimeState);
  const { itemData: logs } = useLogResultsState();
  const viewDisplaySettings = useRecoilValue(viewDisplaySettingsState);
  const setIsLiveTailActive = useSetRecoilState(isLiveTailActiveState);
  const copy = useCopyToClipboard();
  const liveTailUrl = useRecoilValue(liveTailPageUrlState);
  const facetInfo = useRecoilValue(facetInfoState);
  const filter = useRecoilValue(searchParamState(K.URL_PARAM.FILTER));
  const { fetchShortUrlPromise } = useShortUrl();

  const pluginSearchResultsMenuItems = useRecoilValue(pluginSearchResultsMenuItemsState);
  const accountData = useRecoilValue(accountDataState);

  // issue the download request for up to 10000 log lines
  const downloadLogLines = (format: K.DATA_FORMAT) => {
    if (!startTime || !endTime || !requestTime) {
      return;
    }

    const request = generateLogEntriesRequest({
      ascending: false,
      endTime: endTimeNs,
      filter: completeFilter,
      numResults: NUM_DOWNLOAD_ENTRIES,
      offset: '',
      requestTime,
      startTime: startTimeNs,
      teamEmails,
    });

    setDownloadFormat(format);
    setLogEntriesRequest(request);
  };

  // pull log entries out of log entry edges
  const getLogEntries = (logEntryEdges?: Array<LogEntryEdge | PlaceholderLogEntryEdge>): LogEntry[] =>
    (logEntryEdges || []).map((logEntryEdge) => logEntryEdge.logEntry).filter(Boolean) as LogEntry[];

  const logEntriesResult = logEntriesQueryResult?.results?.[0];
  const options = { timezone, facetInfo, filter };

  const copyHandler = (format: K.DATA_FORMAT, type: string, addShortUrl = false) => {
    const logEntries = getLogEntries(logs?.logEntryEdges);
    if (addShortUrl) {
      fetchShortUrlPromise(absoluteUrl).then(({ data }) => {
        const content = formatSearchResults(logEntries, format, settings, { ...options, shortUrl: data?.compressUrl });
        copy(content, type);
      });
    } else {
      const content = formatSearchResults(logEntries, format, settings, options);
      copy(content, type);
    }
  };

  // start the download when the log entries have been fetched
  useConditionalEffect(() => {
    const logEntryEdges = [...(logEntriesResult?.data?.matches ?? [])];
    const logEntries = getLogEntries(logEntryEdges);
    // reverse before processing, if needed
    if (sortOrder === SortOrder.MOST_RECENT_AT_TOP) {
      logEntries.reverse();
    }

    downloadFile({
      content: formatSearchResults(logEntries, downloadFormat, settings, options),
      contentType: K.DATA_FORMAT_CONTENT_TYPE[downloadFormat],
      fileName: getDownloadFilename(downloadFormat, false),
    });
    setLogEntriesRequest(null);
  }, logEntriesResult?.isDone && logEntriesRequest !== null);

  // send any outstanding request to get log entries for download
  let logEntriesRequestManager;
  if (logEntriesRequest) {
    logEntriesRequestManager = <LRQRequestManager key={logEntriesRequest.key} request={logEntriesRequest} />;
  }

  // saved searches
  const searchLink = applySettingsToUrl(absoluteUrl, settings);

  const dashAndAlertsSection: Array<string | ActionMenuItemConfig> = [];
  if (!isCrossTeamSearch) {
    dashAndAlertsSection.push(SearchActions.ALERT);
  }
  dashAndAlertsSection.push(SearchActions.DASHBOARD);

  const saveItems = [SearchActions.SAVE];

  const saveMenuSections: Array<Array<string | ActionMenuItemConfig>> = [saveItems, dashAndAlertsSection];

  if (accountData.isSingularityXDRUser && pluginSearchResultsMenuItems && pluginSearchResultsMenuItems.length > 0) {
    saveMenuSections.push(pluginSearchResultsMenuItems);
  }

  saveMenuSections.push([
    {
      text: 'Download',
      key: 'download',
      clickHandler: () => {},
      subMenuItems: [
        {
          text: 'Text',
          key: K.DATA_FORMAT.TEXT,
          clickHandler: () => downloadLogLines(K.DATA_FORMAT.TEXT),
        },
        {
          text: 'JSON',
          key: K.DATA_FORMAT.JSON,
          clickHandler: () => downloadLogLines(K.DATA_FORMAT.JSON),
        },
        {
          text: 'CSV',
          key: K.DATA_FORMAT.CSV,
          clickHandler: () => downloadLogLines(K.DATA_FORMAT.CSV),
        },
        {
          text: 'Markdown',
          key: K.DATA_FORMAT.MARKDOWN,
          clickHandler: () => downloadLogLines(K.DATA_FORMAT.MARKDOWN),
        },
      ],
      testId: ELEMENT.searchLogsDownload,
    },
    SearchActions.EXPORT,
  ]);

  const shareMenuSections = [
    [
      SearchActions.COPY_LINK,
      {
        text: 'Copy to Clipboard',
        key: 'copy_to_clipboard',
        clickHandler: () => {},
        subMenuItems: [
          {
            text: 'Text',
            key: K.DATA_FORMAT.TEXT,
            clickHandler: () => copyHandler(K.DATA_FORMAT.TEXT, 'text', true),
          },
          {
            text: 'JSON',
            key: K.DATA_FORMAT.JSON,
            clickHandler: () => copyHandler(K.DATA_FORMAT.JSON, 'JSON'),
          },
          {
            text: 'CSV',
            key: K.DATA_FORMAT.CSV,
            clickHandler: () => copyHandler(K.DATA_FORMAT.CSV, 'CSV'),
          },
          {
            text: 'Markdown',
            key: K.DATA_FORMAT.MARKDOWN,
            clickHandler: () => copyHandler(K.DATA_FORMAT.MARKDOWN, 'markdown', true),
          },
        ],
        testId: ELEMENT.searchLogsCopyToClipboard,
      },
    ],
    [SearchActions.SAVE_SHARED],
  ];

  const saveAlertDialogConfig = {
    triggerType: TriggerType.FREQUENCY,
  };

  const onStartLogs = () => {
    const request = generateLogEntriesRequest({
      ascending: true,
      endTime,
      filter: completeFilter,
      numResults: K.NUM_INITIAL_LOG_ENTRIES,
      offset: '',
      requestTime,
      startTime,
      teamEmails,
    });
    resetIsScrollReady();
    resetVisibleCenterIndex();
    setAnchorLogRequests([request]);
    setPaginatedLogRequests(K.EMPTY_ARRAY);
    setTimeMarker(Number(startTime));
  };

  const onEndLogs = () => {
    const request = generateLogEntriesRequest({
      ascending: false,
      endTime: endTimeNs,
      filter: completeFilter,
      numResults: K.NUM_INITIAL_LOG_ENTRIES,
      offset: '',
      requestTime,
      startTime: startTimeNs,
      teamEmails,
    });
    resetIsScrollReady();
    resetVisibleCenterIndex();
    setAnchorLogRequests([request]);
    setPaginatedLogRequests(K.EMPTY_ARRAY);
    setTimeMarker(Number(endTime));
  };

  const group1 = (
    <BootstrapButtonGroup>
      <Button
        size="sm"
        leftIcon={skipPreviousIcon}
        onClick={onStartLogs}
        disabled={isAtStartTime}
        testId={ELEMENT.searchLogsResultsStart}
      >
        Start
      </Button>
      <Button
        size="sm"
        rightIcon={skipNextIcon}
        onClick={onEndLogs}
        disabled={isAtEndTime}
        testId={ELEMENT.searchLogsResultsEnd}
      >
        End
      </Button>
    </BootstrapButtonGroup>
  );

  const group2 = (
    <BootstrapButtonGroup>
      <SearchActionsDropdown
        text="Save"
        leftIcon={starIcon}
        sections={saveMenuSections}
        savedSearchUrl={searchLink}
        saveAlertDialogConfig={saveAlertDialogConfig}
        testId={ELEMENT.searchLogs}
        buttonTestId={ELEMENT.searchLogsSaveButton}
      />
      <SearchActionsDropdown
        text="Share"
        leftIcon={shareIcon}
        sections={shareMenuSections}
        savedSearchUrl={searchLink}
        copyLinkUrl={searchLink}
        testId={ELEMENT.searchLogs}
        buttonTestId={ELEMENT.searchLogsShareButton}
        shouldFetchShortUrlAfterOpen
      />
    </BootstrapButtonGroup>
  );

  const leftItems = [group1, group2];

  const rightItems = [
    <LogLinesToolbarButtons />,
    <Button
      size="sm"
      leftIcon={settingsIcon}
      onClick={() => setShowSettingsDialog(true)}
      testId={ELEMENT.searchLogsResultsSettings}
    >
      Display
    </Button>,
    <Button
      size="sm"
      leftIcon={helpIcon}
      href={HELP_ROUTE.HELP_LOG_OVERVIEW}
      openInNewTab
      testId={ELEMENT.searchLogsResultsHelp}
    >
      Help
    </Button>,
    viewDisplaySettings?.disableLiveTail || isCrossTeamSearch ? null : (
      <Button
        size="sm"
        leftIcon={flashOnIcon}
        href={liveTailUrl}
        onClick={() => setIsLiveTailActive(true)}
        testId={ELEMENT.searchLogsResultsLiveTail}
      >
        Live Tail
      </Button>
    ),
  ].filter(Boolean);

  return (
    <div className="LogResultsToolbar">
      <Toolbar leftItems={leftItems} rightItems={rightItems} />
      <LogViewSettingsDialog show={showSettingsDialog} close={() => setShowSettingsDialog(false)} />
      {logEntriesRequestManager}
    </div>
  );
}

export default LogResultsToolbar;
